import { gql, useApolloClient, useQuery, useSubscription } from '@apollo/client';
import { Box, List, TextButton } from '@zillow/constellation-10';
import { SwipeableList } from '@sandstreamdev/react-swipeable-list';
// import '@sandstreamdev/react-swipeable-list/dist/styles.css';
import { useContext, useEffect, useState } from 'react';
import { Folder, RenterProgressStatus } from '../../types/GraphqlTypes';
import type { ConversationList_GetConversationsQuery } from '../../types/GraphqlTypes';
import {
  ConversationList_GetConversationsDocument,
  ReadRepliedStatus,
  useConversationList_GetConversationsQuery,
} from '../../types/GraphqlTypes';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import { emitConversationItemSelectEvent } from '../../constants/clickstreamEvents/conversationItem';
import { MAX_OLD_CONVERSATION_DAYS } from '../../constants/ConfigConstants';
import { inboxPageIsLoadingVariable, shouldRefetchMessagesVariable } from '../../lib/apollo';
import useDevices from '../../hooks/useDevices';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import ConditionalWrapper from '../ConditionalWrapper';
import ConversationItemContainer from './components/ConversationItemContainer';
import EmptyContainer from './components/EmptyContainer';
import ViewLeads from '../ViewLeads';
import { ConversationItemSkeleton, ConversationListSkeleton } from './skeleton';
import useConversationListHelpers from '../../hooks/useConversationListHelpers';
import getValueFromEnum from '../../utils/getValueFromEnum';
import isFeedId from '../../utils/isFeedId';
import useFeatureFlagsHelper from '../../hooks/useFeatureFlagsHelper';
import { IsVisible } from '@zg-rentals/react-use';
import { logApolloError } from '../../utils/errorUtils';
import { logError } from '@zg-rentals/log-error';
import { useAuth } from '@zg-rentals/auth-comet';
import { getCurrentSessionURL } from '../../utils/fullStory';
import { scrollbarHoverStyles } from '../../styles/scrollbar';

export type ConversationSelectArgs = {
  selectedConversationId: string;
  selectedListingAlias: string;
};

export type ConversationListConversations =
  ConversationList_GetConversationsQuery['ZRM_getLatestConversationsV2']['conversations'];
export type ConversationListConversation = ArrayElement<ConversationListConversations>;

const POLL_LENGTH_WITH_CONVERSATIONS_IN_MINUTES = 1;
const POLL_LENGTH_WITH_CONVERSATIONS_IN_MS = 60 * 1000 * POLL_LENGTH_WITH_CONVERSATIONS_IN_MINUTES;

const ConversationList = () => {
  const apolloClient = useApolloClient();
  const { changeConversation, getFiltersQueryString, updatePath } = useInboxRouterHelper();
  const { isDesktopView, isMobileView } = useDevices();
  const { clearGetLatestConversationsQueryCache } = useConversationListHelpers();
  const { enableWebsocketIntegration } = useFeatureFlagsHelper();

  const {
    listingAlias,
    clientTimeZone,
    daysBack,
    isByProperty,
    folder,
    renterProgressStatus,
    readRepliedStatus,
    conversationId,
    mobileView,
  } = useContext(InboxPageContext);

  const [isLoadingOlderConversations, setIsLoadingOlderConversations] = useState(false);
  const [shouldShowSkeleton, setShouldShowSkeleton] = useState(false);
  const [hasMoreThan5Listings, setHasMoreThan5Listings] = useState(false);
  const [visitedFilters, setVisitedFilters] = useState<Array<string>>([]);
  const hasLoadedOlderConversations = !!daysBack;

  const { user } = useAuth();
  const { features, isEnterpriseCustomer } = user || {};
  const { canAccessMainContacts } = features || {};
  const { data: inboxPageVariables } = useQuery(GET_INBOX_PAGE_VARIABLES, { fetchPolicy: 'cache-only' });
  const { inboxPageConversationIdToFilterBy, inboxPageConversationSearchText, inboxPageShowEmptyContainer } =
    inboxPageVariables;
  const {
    data: conversationsData,
    error,
    loading,
    fetchMore,
    startPolling,
    stopPolling,
  } = useConversationList_GetConversationsQuery({
    variables: {
      conversationId: inboxPageConversationIdToFilterBy || undefined,
      listingAlias: isByProperty ? listingAlias : null,
      clientTimeZone,
      daysBack: hasLoadedOlderConversations ? MAX_OLD_CONVERSATION_DAYS : null,
      folder,
      renterProgressStatus,
      readRepliedStatus,
    },
    ssr: false,
    errorPolicy: 'all',
    onError: (error) => {
      logApolloError(error, {
        details: 'ConversationList - Error getting the conversation list when polling',
        listingAlias: isByProperty ? listingAlias : null,
        clientTimeZone,
        daysBack: hasLoadedOlderConversations ? MAX_OLD_CONVERSATION_DAYS : null,
        folder,
        renterProgressStatus,
        readRepliedStatus,
      });
    },
    pollInterval: enableWebsocketIntegration ? 0 : POLL_LENGTH_WITH_CONVERSATIONS_IN_MS,
    fetchPolicy: 'cache-first',
  });

  const { data: newConversationMessageData } = useSubscription(gql`
    subscription onConversationMessageReceived {
      conversationId
    }
  `);

  useEffect(() => {
    const conversations = conversationsData?.ZRM_getLatestConversationsV2.conversations;

    if (!loading && (!conversations?.length || (listingAlias && !isFeedId(listingAlias) && conversationId))) {
      inboxPageIsLoadingVariable(false);
    }
  }, [loading, listingAlias, conversationId, conversationsData]);

  useEffect(() => {
    if (enableWebsocketIntegration && newConversationMessageData) {
      // Trigger the query to fetch /getLatest when a new conversation/message is received through subscription
      apolloClient.query({
        query: ConversationList_GetConversationsDocument,
        variables: {
          conversationId: inboxPageConversationIdToFilterBy || undefined,
          listingAlias: isByProperty ? listingAlias : null,
          clientTimeZone,
          daysBack: hasLoadedOlderConversations ? MAX_OLD_CONVERSATION_DAYS : null,
          folder,
          renterProgressStatus,
          readRepliedStatus,
        },
        fetchPolicy: 'network-only',
      });

      // If the incoming conversationId matches the currently viewing one, refetch the messages for the currently viewing conversation
      if (newConversationMessageData.conversationId === conversationId) {
        shouldRefetchMessagesVariable(true);
      }
    }
  }, [newConversationMessageData, enableWebsocketIntegration]);

  // If value of feature flag enableWebsocketIntegration changes, update the polling accordingly
  useEffect(() => {
    if (enableWebsocketIntegration) {
      stopPolling();
    } else {
      startPolling(POLL_LENGTH_WITH_CONVERSATIONS_IN_MS);
    }
    // Cleanup function to stop polling when the component unmounts or the value changes
    return () => stopPolling();
  }, [enableWebsocketIntegration, startPolling, stopPolling, POLL_LENGTH_WITH_CONVERSATIONS_IN_MS]);

  const refetchConversationsAsNetworkOnly = async () => {
    setShouldShowSkeleton(true);
    await apolloClient.query({
      query: ConversationList_GetConversationsDocument,
      variables: {
        listingAlias: isByProperty ? listingAlias : null,
        clientTimeZone,
        daysBack: hasLoadedOlderConversations ? MAX_OLD_CONVERSATION_DAYS : null,
        folder,
        renterProgressStatus,
        readRepliedStatus,
        skipMerge: true,
      },
      fetchPolicy: 'network-only',
    });
    setShouldShowSkeleton(false);
  };

  useEffect(() => {
    if (!loading) refetchConversationsAsNetworkOnly();
  }, [folder]);

  useEffect(() => {
    // Fetch conversations when applying the unread filter in case a user
    // recently marked a conversation as unread
    if (readRepliedStatus === ReadRepliedStatus.Unread) {
      refetchConversationsAsNetworkOnly();
    }
  }, [readRepliedStatus]);

  useEffect(() => {
    // Keeps track of filter combinations that have been visited & cached before user has loaded older conversations
    // in order to clear them from cache once user loads older conversations
    if (!hasLoadedOlderConversations) {
      const currentFilters = getAppliedFilters({
        folder,
        isByProperty,
        listingAlias,
        readRepliedStatus,
        renterProgressStatus,
      });

      if (!visitedFilters.includes(currentFilters)) {
        setVisitedFilters([...visitedFilters, currentFilters]);
      }
    }
  }, [hasLoadedOlderConversations, folder, isByProperty, listingAlias, readRepliedStatus, renterProgressStatus]);

  useEffect(() => {
    const conversations = conversationsData?.ZRM_getLatestConversationsV2.conversations;
    const totalListingNumber = Number(user?.summary?.totalListingCount);

    // Client side redirect to a conversation
    if (conversations?.length && (!conversationId || (listingAlias && isFeedId(listingAlias)))) {
      const { conversationId, listingAlias } = conversations[0];
      changeConversation({
        conversationId,
        listingAlias,
        replacePath: true,
      });
    } else if (!hasLoadedOlderConversations && conversations?.length === 0) {
      if (totalListingNumber < 5) {
        setIsLoadingOlderConversations(true);
        fetchOlderConversations();
      } else {
        setHasMoreThan5Listings(true);
      }
    }
  }, [conversationsData]);

  const conversations = conversationsData?.ZRM_getLatestConversationsV2.conversations || [];
  const showLoadMore =
    !conversationsData?.ZRM_getLatestConversationsV2.pagination?.hasNextPage && !hasLoadedOlderConversations;
  const showEmptyContainer = !conversations.length || inboxPageShowEmptyContainer;

  // Used when refetching conversations
  if (shouldShowSkeleton) {
    return <ConversationListSkeleton />;
  }

  if ((loading || isLoadingOlderConversations) && conversations.length === 0) {
    inboxPageIsLoadingVariable(true);
    return <ConversationListSkeleton />;
  }

  if (error && !conversations.length) {
    logApolloError(error, {
      details: 'ConversationList - Error loading conversations for conversations list',
      listingAlias: isByProperty ? listingAlias : null,
      clientTimeZone,
      daysBack: hasLoadedOlderConversations ? MAX_OLD_CONVERSATION_DAYS : null,
      folder,
      renterProgressStatus,
      readRepliedStatus,
      numberOfConversations: conversations.length,
    });
  }

  // Used when 0 conversations are returned from initial fetch, and we want to fetch older than 90 day conversations
  async function fetchOlderConversations(shoudUpdatePathWithDaysBack = false) {
    if (!isLoadingOlderConversations) {
      setIsLoadingOlderConversations(true);
    }

    let paginationStartIndex = null;

    if (conversationsData?.ZRM_getLatestConversationsV2.conversations.length) {
      paginationStartIndex =
        conversationsData.ZRM_getLatestConversationsV2.conversations[
          conversationsData.ZRM_getLatestConversationsV2.conversations.length - 1
        ].conversationId;
    }

    await apolloClient.query({
      query: ConversationList_GetConversationsDocument,
      variables: {
        listingAlias: isByProperty ? listingAlias : null,
        clientTimeZone,
        folder,
        renterProgressStatus,
        readRepliedStatus,
        daysBack: MAX_OLD_CONVERSATION_DAYS,
        paginationStartIndex,
      },
      fetchPolicy: 'network-only',
    });

    inboxPageIsLoadingVariable(false);
    setIsLoadingOlderConversations(false);

    // Update path to set daysBack param to MAX_OLD_CONVERSATION_DAYS to prevent
    // fetching older conversations again throughout user session
    if (shoudUpdatePathWithDaysBack) {
      updatePath({
        conversationId,
        listingAlias,
        byProperty: isByProperty,
        filtersQueryString: getFiltersQueryString({
          byProperty: isByProperty,
          daysBack: MAX_OLD_CONVERSATION_DAYS,
          folder,
          mobileView: mobileView || InboxPageMobileView.ConversationList,
          readRepliedStatus,
          renterProgressStatus,
        }),
      });
    }

    // Loop through visitedFilterQueryParams and clear cache for each query except for
    // the current filter combination to prevent stale data in cache
    const currentFilter = getAppliedFilters({
      folder,
      isByProperty,
      listingAlias,
      readRepliedStatus,
      renterProgressStatus,
    });

    visitedFilters &&
      visitedFilters.forEach((visitedFilter) => {
        if (visitedFilter === currentFilter) {
          return;
        }
        const visitedFilterObj = Object.fromEntries(new URLSearchParams(visitedFilter));
        const { folder, listingAlias, rps, rrs } = visitedFilterObj;

        clearGetLatestConversationsQueryCache({
          folder: getValueFromEnum(folder, Folder),
          listingAlias,
          readRepliedStatus: getValueFromEnum(rrs, ReadRepliedStatus),
          renterProgressStatus: getValueFromEnum(rps, RenterProgressStatus),
        });
      });
  }

  const handleFetchOlderConversations = () => fetchOlderConversations(true);

  const handleConversationSelect = async ({
    selectedConversationId,
    selectedListingAlias,
  }: ConversationSelectArgs): Promise<void> => {
    if (selectedConversationId === conversationId && isDesktopView) {
      return;
    }

    emitConversationItemSelectEvent(user);

    changeConversation({
      conversationId: selectedConversationId,
      listingAlias: selectedListingAlias,
      newMobileView: InboxPageMobileView.MessageList,
    });
  };

  const handleFetchMoreConversations = async (isVisible: boolean, retry = false): Promise<void> => {
    if (!isVisible) {
      return;
    }

    try {
      await fetchMore({
        variables: {
          listingAlias: isByProperty ? listingAlias : null,
          clientTimeZone,
          folder,
          renterProgressStatus,
          readRepliedStatus,
          daysBack: hasLoadedOlderConversations ? MAX_OLD_CONVERSATION_DAYS : null,
          paginationStartIndex: conversations.length ? conversations[conversations.length - 1].conversationId : null,
        },
      });
    } catch (error) {
      if (!retry) {
        return handleFetchMoreConversations(isVisible, true);
      }
      logError({
        error,
        context: {
          details: 'ConversationList - Error fetching more conversations',
          fullStorySessionUrl: getCurrentSessionURL(),
          listingAlias: isByProperty ? listingAlias : null,
          clientTimeZone,
          folder,
          renterProgressStatus,
          readRepliedStatus,
          daysBack: hasLoadedOlderConversations ? MAX_OLD_CONVERSATION_DAYS : null,
          paginationStartIndex: conversations.length ? conversations[conversations.length - 1].conversationId : null,
        },
      });
    }
  };

  return (
    <>
      {canAccessMainContacts && <ViewLeads isEnterpriseCustomer={Boolean(isEnterpriseCustomer)} />}

      {showEmptyContainer ? (
        <EmptyContainer
          folder={folder}
          hasConversationSearchText={!!inboxPageConversationSearchText}
          hasMoreThan5Listings={hasMoreThan5Listings}
          isByProperty={isByProperty}
          readRepliedStatus={readRepliedStatus}
          renterProgressStatus={renterProgressStatus}
          showErrorMessage={!!error}
        />
      ) : (
        <List.Root
          aria-label="conversations"
          data-testid="conversation-list"
          appearance="none"
          css={{
            overflowY: 'auto',
            ...scrollbarHoverStyles,
          }}
        >
          <ConditionalWrapper
            shouldRenderWrapperElement={isMobileView}
            wrapper={(children) => <SwipeableList threshold={0.75}>{children}</SwipeableList>}
          >
            <>
              {conversations.map((conversation) => (
                <ConversationItemContainer
                  key={conversation.conversationId}
                  conversation={conversation}
                  onSelect={handleConversationSelect}
                />
              ))}

              {conversationsData?.ZRM_getLatestConversationsV2.pagination?.hasNextPage && (
                <IsVisible onChange={handleFetchMoreConversations} options={{ rootMargin: '-100px 0px 0px 0px' }}>
                  <ConversationItemSkeleton />
                  <ConversationItemSkeleton />
                </IsVisible>
              )}

              {showLoadMore && (
                <Box
                  css={{
                    paddingBlock: 'default',
                    textAlign: 'center',
                  }}
                >
                  <TextButton
                    onClick={handleFetchOlderConversations}
                    disabled={isLoadingOlderConversations}
                    css={{ textAlign: 'center' }}
                  >
                    {isLoadingOlderConversations ? 'Loading older conversations...' : 'Load older conversations'}
                  </TextButton>
                </Box>
              )}
            </>
          </ConditionalWrapper>
        </List.Root>
      )}
    </>
  );
};

export default ConversationList;

const GET_INBOX_PAGE_VARIABLES = gql`
  query {
    inboxPageConversationIdToFilterBy @client
    inboxPageConversationSearchText @client
    inboxPageShowEmptyContainer @client
  }
`;

/**
 *
 * @description Generates query strings of folder+filter combinations for ConversationList to keep track of
 * in order to clear existing cache for respective combinations once the user loads conversations older than 90 days
 */
const getAppliedFilters = ({
  folder,
  listingAlias,
  renterProgressStatus,
  readRepliedStatus,
  isByProperty,
}: {
  folder: Folder;
  listingAlias?: string;
  renterProgressStatus: RenterProgressStatus;
  readRepliedStatus: ReadRepliedStatus;
  isByProperty: boolean;
}) => {
  return `folder=${folder}&rps=${renterProgressStatus}&rrs=${readRepliedStatus}${
    isByProperty ? `&listingAlias=${listingAlias}` : ''
  }`;
};
